export const enUS = {
  seo: {
    title: 'StellarTrail | Outdoor preparation companion',
    description: 'StellarTrail helps you manage personal gear, confirm packing lists, organize trip prep, and review practical knot skills before heading outdoors.'
  },
  nav: {
    product: 'Product intro',
    gear: 'Personal gear',
    packing: 'Packing lists',
    trips: 'Trip prep',
    skills: 'Knot skills',
    entry: 'Downloads',
    web: 'Web App',
    downloads: 'Downloads',
    docs: 'API Docs',
    privacy: 'Privacy Policy',
    language: 'Language'
  },
  jump: {
    label: 'Page quick jumps',
    trigger: 'Expand page quick jumps',
    title: 'On this page',
    home: 'Home'
  },
  hero: {
    eyebrow: 'StellarTrail',
    title: 'Prepare gear, lists, trips, and knots before you head out',
    subtitle: 'A lightweight companion for outdoor preparation: track personal gear, confirm packing lists, organize trip prep, and review field-ready knot skills before departure.',
    primaryCta: 'View downloads',
    secondaryCta: 'Open Web app',
    stats: [
      { value: 'Organize', label: 'Gear and lists' },
      { value: 'Confirm', label: 'Trip prep' },
      { value: 'Review', label: 'Knot skills' }
    ]
  },
  product: {
    eyebrow: 'Product focus',
    title: 'Turn scattered pre-departure tasks into steps you can check',
    body: 'StellarTrail focuses on the preparation phase before heading outdoors. The site highlights product-visible capabilities and separates available entries from feature previews with clear status labels.',
    statusLabels: {
      available: 'Available now',
      preview: 'Feature preview'
    },
    capabilities: {
      gear: {
        title: 'Personal gear',
        subtitle: 'Track each item by category, status, weight, value, and storage location.',
        body: 'Keep headlamps, packs, stoves, and other recurring gear in one personal library so pre-departure checks start from your own inventory.',
        bullets: ['Gear counts and weight summaries', 'Category, status, and search filters', 'Gear detail and editing flow']
      },
      packing: {
        title: 'Packing lists',
        subtitle: 'Confirm departure items by trip and scenario.',
        body: 'Turn personal gear into a packing list, then check total weight, packed weight, and missing items before you leave.',
        bullets: ['Build lists by scenario', 'Mark packed items one by one', 'Track weight and progress']
      },
      trips: {
        title: 'Trip prep',
        subtitle: 'Keep solo prep and group coordination in one view.',
        body: 'Trip pages collect dates, members, and readiness signals so you can confirm gear, skills, weather, and safety prep before departure.',
        bullets: ['Solo prep and group coordination', 'Member and day summaries', 'Readiness cues']
      },
      skills: {
        title: 'Knot skills',
        subtitle: 'Review practical outdoor skills before departure.',
        body: 'The current skill library focuses on knots for camping, fishing, connecting, and securing, with quick entry into detailed practice views.',
        bullets: ['Knot category entry', 'Favorite skills list', 'Fast review before departure']
      }
    }
  },
  gear: {
    eyebrow: 'Personal gear',
    title: 'Keep every item in a gear library you can track',
    body: 'Record category, status, weight, and value for each item. Whether you organize on desktop or check on mobile, the available gear stays easy to scan.',
    bullets: ['View item count, category count, total weight, and value', 'Search by name, brand, or model', 'Move from personal gear into atlas or packing lists']
  },
  packing: {
    eyebrow: 'Packing lists',
    title: 'Confirm every item before departure',
    body: 'Packing lists break preparation into checkable items, helping you confirm gear by destination, day count, and scenario before you walk out the door.',
    bullets: ['Create packing lists for a trip', 'Track total and packed weight', 'Keep list state close on mobile']
  },
  trips: {
    eyebrow: 'Trip prep',
    title: 'Make trip details, members, and readiness easier to see',
    body: 'Trip prep gathers dates, members, and readiness progress so you can quickly see what still needs attention before departure.',
    bullets: ['Solo prep and group coordination entries', 'Date, member, and day summaries', 'Pre-departure checks for gear, skills, weather, and safety']
  },
  skills: {
    eyebrow: 'Knot skills',
    title: 'Start with knots that are useful in the field',
    body: 'The current skill library focuses on knots for camping, fishing, connecting, and securing, making it useful for quick review before heading outdoors.',
    bullets: ['Current capability: knot tutorials', 'Favorite common skills', 'Fast review before departure']
  },
  screenshots: {
    androidGearAlt: 'Android personal gear interface',
    androidPackingAlt: 'Android packing list interface',
    androidTripsAlt: 'Android trip preparation interface',
    androidSkillsAlt: 'Android knot skills interface',
    wechatGearAlt: 'WeChat Mini Program gear management interface',
    wechatKnotsAlt: 'WeChat Mini Program knot skills interface',
    webGearAlt: 'Web gear management interface',
    webGearFormAlt: 'Web add-gear form interface'
  },
  entry: {
    eyebrow: 'Downloads',
    title: 'Mobile entries live on the downloads page',
    body: 'The Web app is live for browser-based personal gear organization and public content. Android install and WeChat Mini Program entries are grouped on the downloads page.',
    badge: 'Multi-platform',
    hint: 'The homepage does not show placeholder mobile downloads or placeholder mini program codes; mobile entries are updated on the downloads page.',
    channelsLabel: 'Supported platform entries',
    channels: [
      {
        title: 'Web access',
        body: 'The Web app is live for organizing gear and viewing public content on a larger screen.',
        href: 'https://app.stellartrail.cn/',
        action: 'Open Web app',
        external: true
      },
      {
        title: 'Mobile downloads',
        body: 'Android install and WeChat Mini Program entries are collected on one page.',
        href: 'downloads/',
        action: 'View downloads',
        external: false
      }
    ]
  },
  downloads: {
    seo: {
      title: 'StellarTrail | Downloads and mobile entries',
      description: 'Check StellarTrail Web, Android, and WeChat Mini Program entry status. Mobile entries are grouped on this page.'
    },
    nav: {
      label: 'Downloads navigation',
      brand: 'StellarTrail',
      backHome: 'Back to site',
      web: 'Web App',
      docs: 'API Docs',
      privacy: 'Privacy Policy'
    },
    hero: {
      eyebrow: 'Downloads',
      title: 'Mobile entries are collected here',
      body: 'Android install and WeChat Mini Program entries will be updated on this page. The Web app is available now; unpublished mobile entries stay clearly marked as preparing.',
      primaryCta: 'View mobile entries',
      secondaryCta: 'Open Web app'
    },
    summary: {
      title: 'Entry status',
      items: ['Web app available now', 'Android install preparing', 'WeChat Mini Program preparing']
    },
    channelsLabel: 'Downloads and entries',
    channels: {
      android: {
        platform: 'Android',
        status: 'Preparing',
        title: 'Android install',
        body: 'Android product views are available as examples. The public package, app-store listing, or install method will be added here when ready.',
        bullets: ['Central install status', 'No placeholder download link', 'Real entry added after publication'],
        action: 'Not open yet',
        note: 'The Android entry will be updated here when ready.'
      },
      wechat: {
        platform: 'WeChat Mini Program',
        status: 'Preparing',
        title: 'WeChat Mini Program entry',
        body: 'Designed for quick access inside WeChat. The mini program code will be shown here when ready.',
        bullets: ['Central mini program status', 'No placeholder mini program code', 'Real entry added after publication'],
        action: 'Not open yet',
        note: 'The WeChat Mini Program entry will be updated here when ready.'
      },
      web: {
        platform: 'Web',
        status: 'Available now',
        title: 'Web app',
        body: 'The Web app is already available for organizing gear and viewing public content in a browser.',
        bullets: ['Desktop gear organization', 'Public content browsing', 'No install needed'],
        action: 'Open Web app',
        note: 'Use the Web app while mobile entries are being prepared.'
      }
    },
    footerNote: 'No fake download link or fake mini program code is shown; unpublished entries are clearly marked.'
  },
  privacy: {
    seo: {
      title: 'StellarTrail | Privacy Policy',
      description: 'Learn how 寻径星野/StellarTrail collects, uses, stores, protects, and deletes personal information.'
    },
    nav: {
      label: 'Privacy policy navigation',
      brand: 'StellarTrail',
      backHome: 'Back to site',
      downloads: 'Downloads',
      docs: 'API Docs'
    },
    hero: {
      eyebrow: 'Privacy',
      title: 'Privacy Policy',
      body: 'This policy explains how 寻径星野/StellarTrail handles information related to you when providing account, gear, packing list, trip preparation, trail, knot skill, and feedback services.'
    },
    meta: {
      label: 'Policy information',
      operator: 'Operator',
      operatorName: '寻径星野/StellarTrail',
      effectiveDate: 'Effective date',
      effectiveDateValue: '2026-06-15',
      contact: 'Privacy contact',
      contactEmail: 'abuse@stellartrail.cn'
    },
    summary: {
      title: 'Key points',
      items: ['We process personal information only for accounts, sync, product features, security, support, and compliance needs.', 'We do not sell personal information or use it for third-party advertising profiles.', 'Account deletion is supported: you can start deletion in the app or contact abuse@stellartrail.cn for help.']
    },
    sections: [
      {
        id: 'scope',
        title: '1. Scope',
        paragraphs: ['This policy applies to StellarTrail official site, Web app, Android app, iOS app, macOS app, WeChat Mini Program, and related product entries. Feature availability may differ by platform, and the information handled depends on the features you use.']
      },
      {
        id: 'data',
        title: '2. Information We May Handle',
        paragraphs: ['To provide account sign-in, data sync, outdoor preparation, and customer support, we may handle the following information:'],
        bullets: ['Account and sign-in information: username, nickname, email address, phone number, WeChat login identifier, verification result, access-token hash, and session state. For password-based sign-in, user passwords are stored only after server-side SHA-256 hashing; plaintext passwords are not stored.', 'Profile and outdoor profile information: avatar, real name, gender, birth date, height, contact details, emergency contact, blood type, medical history, allergy history, diet preference, insurance information, and outdoor experience notes that you choose to provide.', 'Product content data: personal gear, gear weight and price, storage location, packing lists, trip preparation, member collaboration, trail files, map annotations, knot favorites, product-plan votes or subscriptions, and related content.', 'Uploads and feedback: avatar images, feedback images, feedback text, contact details, page path, client platform, client version, and device model.', 'Permissions and device-related data: the Android app may use coarse or precise location after your authorization for map, trail, or current-location features; when you import GPX, KML, KMZ, FIT, or similar files, we read the files you choose or share with the app.', 'Security and statistics: to protect service security and understand API operation, we record minimized aggregate API usage statistics such as authenticated user ID, HTTP method, endpoint pattern, status code, date bucket, and request count; we do not record request bodies, response bodies, tokens, cookies, IP addresses, or User-Agent values.']
      },
      {
        id: 'use',
        title: '3. How We Use Information',
        paragraphs: ['We use the information above for these purposes:'],
        bullets: ['Create and maintain accounts, complete sign-in, verify codes, reset passwords, and bind email addresses or phone numbers.', 'Sync and display your gear, packing lists, trips, trails, knot favorites, and other outdoor preparation content.', 'Process avatars, feedback images, and feedback content so we can investigate issues and respond to support requests.', 'Maintain service security, identify abnormal sign-ins, prevent abuse, troubleshoot failures, and satisfy audit or legal compliance requirements.', 'Generate aggregate statistics without directly identifying individuals, helping us understand feature usage and service stability.']
      },
      {
        id: 'sharing',
        title: '4. Sharing and Processing by Service Providers',
        paragraphs: ['We do not sell personal information. To provide the service, we may use the following providers or disclose necessary information within the required scope:'],
        bullets: ['Cloud servers, databases, object storage, and CDN services used to store account data, images, trail files, and public content media.', 'Email and SMS providers used to send registration, login, binding, or password reset verification codes.', 'WeChat platform capabilities used to complete WeChat Mini Program sign-in and account identification.', 'App stores, review platforms, or compliance organizations used for app publication, review, dispute handling, and safety compliance.', 'Information required by applicable laws, regulations, regulators, courts, or administrative authorities.']
      },
      {
        id: 'security',
        title: '5. Retention and Security',
        paragraphs: ['We use reasonable technical and organizational safeguards to protect personal information, including access control, encrypted transmission, SHA-256 password hashing before database storage, token-hash storage, image type validation, upload quotas, and minimized logging. We retain information only for as long as needed for the purposes described in this policy. When information is no longer needed, or when you lawfully request deletion and no retention reason applies, we delete or anonymize it.']
      },
      {
        id: 'delete-account',
        title: '6. Access, Correction, and Account Deletion',
        paragraphs: ['StellarTrail supports account deletion. You can start account deletion in the app. If you cannot use the in-app entry, contact abuse@stellartrail.cn and we will handle account and related personal-data deletion requests.', 'You may also use the same email address to request access, correction, completion, deletion, authorization withdrawal, or privacy help. To protect account security, we may verify your account identity before processing the request.']
      },
      {
        id: 'children',
        title: '7. Children',
        paragraphs: ['StellarTrail is intended for users who can reasonably judge outdoor activity risks. If you are under 14, use the service only with guardian consent and guidance. Guardians may contact abuse@stellartrail.cn if they believe a child’s information was submitted improperly.']
      },
      {
        id: 'updates',
        title: '8. Updates and Contact',
        paragraphs: ['We may update this policy when product features, laws, or operations change. Material changes will be communicated through the official site or appropriate in-product notices. For privacy questions, complaints, account deletion, or personal information rights requests, contact abuse@stellartrail.cn.']
      }
    ]
  },
  docs: {
    seo: {
      title: 'StellarTrail API Reference',
      description: 'A static API reference generated from the current StellarTrail backend source: registered paths, request bodies, responses, and errors.'
    },
    nav: {
      label: 'API reference navigation',
      brand: 'StellarTrail Docs',
      backHome: 'Back to site'
    },
    hero: {
      eyebrow: 'Developer docs',
      title: 'API Reference',
      body: 'This page lists the service endpoints registered in the current backend source. Examples use paths and placeholder values only; the production origin is not displayed here.'
    },
    toc: {
      label: 'Table of contents'
    },
    source: {
      title: 'Source information',
      repository: 'Source repository',
      inspectedHead: 'Inspected version',
      inspectedAt: 'Inspected date',
      endpointCount: 'Endpoint count'
    },
    sections: {
      overview: {
        title: 'Overview',
        body: 'This reference is synchronized from the backend Axum registration, request DTOs, response DTOs, and common error model. It covers service status, authentication, public content, knot skills, gear library, uploads, and feedback.',
        count: '{count} registered endpoints are listed.',
        note: 'Page load does not request the service. A request is sent only after you enter a service origin and click send.'
      },
      authentication: {
        title: 'Authentication',
        body: 'Public endpoints do not require sign-in. User-scoped endpoints use Authorization Bearer. Administrator endpoints require an authenticated administrator. Skill content accepts X-StellarTrail-Locale or Accept-Language for zh-CN / en.'
      },
      endpoints: {
        title: 'Endpoints',
        body: 'Registered request paths are grouped by capability below.'
      },
      errors: {
        title: 'Error responses',
        body: 'Service errors use a common JSON shape with code and message, plus fields, captcha, or parameter when applicable.'
      },
      config: {
        title: 'Access configuration',
        body: 'The production origin is stored only in deployment-side or ignored local config. It must not be committed, rendered, or bundled. Use your own environment variable and append the path when calling the service.'
      }
    },
    groupTitles: {
      system: 'System status',
      auth: 'Authentication and account',
      content: 'Public content',
      skills: 'Knot skills',
      admin: 'Admin content',
      gear: 'Gear library',
      uploads: 'Image uploads',
      feedback: 'User feedback'
    },
    authLabels: {
      public: 'Public',
      bearer: 'Bearer sign-in required',
      admin: 'Administrator required'
    },
    endpointSummaries: {
      healthz: 'Service health',
      meta: 'Service metadata',
      authWechatLogin: 'WeChat Mini Program login',
      authEmailVerificationCode: 'Send registration email code',
      authEmailLoginCode: 'Send email login code',
      authEmailLogin: 'Email code login',
      authPasswordResetCode: 'Send password reset code',
      authPasswordReset: 'Reset password',
      authRegister: 'Register account',
      authPasswordLogin: 'Account password login',
      authRefresh: 'Refresh session',
      authCaptcha: 'Create image captcha',
      gearTemplatesList: 'Gear preparation template list',
      gearTemplatesDetail: 'Gear preparation template detail',
      skillsCategories: 'Outdoor skill categories',
      skillsKnotsList: 'Knot list',
      skillsKnotsDetail: 'Knot detail',
      adminKnotMediaUpload: 'Upload knot media asset',
      gearsCategories: 'My gear category counts',
      gearsStats: 'My gear statistics',
      gearsExport: 'Export gear CSV',
      gearsImport: 'Import gear JSON',
      gearsList: 'My gear list',
      gearsCreate: 'Create gear',
      gearsDetail: 'Gear detail',
      gearsUpdate: 'Update gear',
      gearsArchive: 'Archive gear',
      gearsRestore: 'Restore gear',
      uploadsCreate: 'Upload feedback image',
      uploadsDownload: 'Read uploaded image',
      feedbackCreate: 'Submit feedback'
    },
    labels: {
      group: 'Group',
      auth: 'Auth',
      responseStatus: 'Response status',
      contentType: 'Content type',
      responseType: 'Response type',
      query: 'Query parameters',
      headers: 'Headers',
      requestBody: 'Request body',
      responseBody: 'Response body',
      noBody: 'No response body',
      tryRequest: 'Try request',
      tryRequestNote: 'Uses the shared service address above. Requests are sent only after you click send; the page does not save the address, headers, or response.',
      serviceOrigin: 'Shared service address',
      serviceOriginPlaceholder: 'https://your-api.example.com',
      serviceOriginHelp: 'Enter this once and all endpoint runners below will reuse it. The page does not save this address.',
      pathParams: 'Path parameters',
      pathParamLabel: '{name} path parameter',
      queryParams: 'Query parameter input',
      queryParamLabel: '{name} query parameter',
      extraQuery: 'Additional query string',
      extraQueryPlaceholder: 'foo=bar&limit=20',
      headersInput: 'Header input',
      headersPlaceholder: 'Authorization: Bearer ...\nX-StellarTrail-Locale: en',
      requestBodyInput: 'Request body input',
      fileInput: 'File',
      sendRequest: 'Send request',
      sending: 'Sending…',
      requestUrl: 'Request URL',
      response: 'Response',
      responseHeaders: 'Response headers',
      responseBodyResult: 'Response body',
      fillServiceOrigin: 'Fill in the service address first',
      invalidServiceOrigin: 'Service address must be an http(s) URL without query string or fragment.',
      fillPathParam: 'Fill in the {name} path parameter',
      invalidHeader: 'Header format must be Name: value',
      invalidJson: 'Request body must be valid JSON',
      networkError: 'Request failed: {message}',
      noResponseBody: 'No response body',
      serviceSettingsTitle: 'Shared service address',
      serviceSettingsNote: 'Fill in the service address once here. Every endpoint runner below will reuse it, and leaving it empty prevents requests.'
    }
  },
  footer: {
    tagline: 'StellarTrail',
    caption: 'Outdoor preparation starts with checklists and knots',
    rights: 'All rights reserved.',
    privacyPolicy: 'Privacy Policy'
  },
  language: {
    current: 'English',
    switchTo: '切换到中文'
  }
} as const;
